import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useTheme } from '../../hooks/useTheme';
import type { ThemeMode } from '../../hooks/useTheme';

/**
 * Theme options surfaced in the Appearance section (#1825). Mirrors the
 * UserMenu treatment so admins moving between the avatar dropdown and the
 * Settings page see the same control.
 */
const THEME_OPTIONS: { value: ThemeMode; label: string }[] = [
    { value: 'system', label: 'System' },
    { value: 'light', label: 'Light' },
    { value: 'dark', label: 'Dark' },
];

/**
 * Appearance panel for the Settings sub-navigation (#1925).
 *
 * Hosts the theme toggle that previously lived inline on the Settings root
 * (see #1825). The control itself is unchanged — only the layout container
 * moves under the new sub-nav.
 *
 * Whether the theme toggle should ultimately move to the avatar menu is a
 * separate UX decision; for this PR we keep it where admins already know to
 * find it.
 */
export default function AppearancePanel() {
    const { themeMode, setThemeMode } = useTheme();

    return (
        <Paper sx={{ p: 3 }}>
            <Typography variant="h6" component="h2" gutterBottom>
                Appearance
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} data-testid="settings-panel-description">
                Choose how the app looks. &ldquo;System&rdquo; follows your operating system&apos;s light or dark setting.
            </Typography>
            <Typography variant="caption" component="div" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>
                Theme
            </Typography>
            <Box className="segmented-control" role="group" aria-label="Theme">
                {THEME_OPTIONS.map(({ value, label }) => (
                    <button
                        key={value}
                        type="button"
                        className={`segmented-control__option${themeMode === value ? ' segmented-control__option--active' : ''}`}
                        aria-pressed={themeMode === value}
                        onClick={() => setThemeMode(value)}
                    >
                        {label}
                    </button>
                ))}
            </Box>
        </Paper>
    );
}
